import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Box,
  MenuItem,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import React, { useState } from "react";

import { fakePlaidImport } from "../../api/plaid";
import {
  csvAmountSignConventionFromTypeKey,
  deriveAccountTypeKeyFromApi,
  plaidTypeSubtypeFromSubtypeValue,
} from "../../utils/bankAccountTypeUtils";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export const FakePlaidDialog = ({ open, onClose, customer_id, onSuccess }) => {
  const [bankName, setBankName] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountSubtype, setAccountSubtype] = useState("checking");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    setFile(f || null);
  };

  const handleCloseSnackbar = (_event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!customer_id) {
      setSnackbarMsg(
        "Missing customer_id (select a customer / resume onboarding, then try again)."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!bankName || !accountName || !file) {
      setSnackbarMsg("Please provide bank name, account name, and a csv file.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("bank_name", bankName);
      fd.append("account_name", accountName);
      fd.append("file", file);
      fd.append("customer_id", customer_id);

      const { type: typeValue, subtype: subtypeValue } =
        plaidTypeSubtypeFromSubtypeValue(accountSubtype);
      fd.append("account_type", typeValue);
      fd.append("account_subtype", subtypeValue);

      const typeKey = deriveAccountTypeKeyFromApi({ accountSubtype: subtypeValue });
      fd.append(
        "csv_amount_sign_convention",
        csvAmountSignConventionFromTypeKey(typeKey)
      );

      const res = await fakePlaidImport(fd);
      const { data, status } = res;
      if (status === 202) {
        const msg = data?.status ? `Import ${data.status}` : "Upload accepted";
        setSnackbarMsg(msg);
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        onSuccess?.();

        // close dialog after a short delay so user sees toast
        setTimeout(() => onClose?.(), 1200);
      } else {
        setSnackbarMsg("Unexpected response from server.");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error("Failed preparing fake Plaid import", err);
      const data = err?.response?.data;
      const detail = data?.detail;
      const httpStatus = err?.response?.status;

      let msg = "";
      if (typeof data === "string" && data.trim()) {
        msg = data;
      } else if (detail) {
        msg = Array.isArray(detail) ? detail.join(" ") : String(detail);
      } else if (data && typeof data === "object") {
        msg = Object.entries(data)
          .map(([key, value]) => {
            if (Array.isArray(value)) return `${key}: ${value.join(", ")}`;
            if (value && typeof value === "object")
              return `${key}: ${JSON.stringify(value)}`;
            return `${key}: ${String(value)}`;
          })
          .join(" | ");
      }

      if (!msg) msg = err?.message || "Failed preparing fake import.";
      if (httpStatus && msg && !msg.includes(String(httpStatus))) {
        msg = `${msg} (${httpStatus})`;
      }

      setSnackbarMsg(msg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={Boolean(open)} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Fake Plaid Import</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Bank Name"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Bank Account Name"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Account Type"
                value={accountSubtype}
                onChange={(e) => setAccountSubtype(e.target.value)}
                fullWidth
                select
              >
                <MenuItem value="checking">Checking</MenuItem>
                <MenuItem value="savings">Savings</MenuItem>
                <MenuItem value="credit card">Credit Card</MenuItem>
              </TextField>
              <div>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  CSV File
                </Typography>
                <input accept=".csv" type="file" onChange={handleFileChange} />
                {file && (
                  <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                    Selected: {file.name}
                  </Typography>
                )}
              </div>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={submitting} variant="outlined">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} variant="contained">
              {submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                "Upload"
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </>
  );
};
